import { HttpParams, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError as observableThrowError } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { IPlayedTicketTO, IPlayedTicketWithGroupsTO } from 'src/app/models/ticket/transportObjects/playedTicketTO.interface';
import { LoaderService } from 'src/app/services/loader.service';
import { NotificationService } from 'src/app/services/notification.service';
import { BaseService } from 'src/app/services/providers/base.service';
import { UserActivityService } from 'src/app/services/userActivity.service';
import { StorageService } from 'src/app/services/util/storage.service';
import { IBaseResponse } from '../../models/baseResponse.interface';
import { ICommissionReport } from '../../models/commissions/commissionReport.interface';
import { ICommissionEstimateReportTO } from '../../models/commissions/commissionsTO/commissionEstimateReportTO.interface';
import { ICommissionReportTO } from '../../models/commissions/commissionsTO/commissionReportTO.interface';
import { ISalesReportsByAffiliateDA } from '../../models/salesReport/DAObjects/salesReportsByAffiliateDA.interface';
import { IAffiliatePlayedTicket } from '../../models/ticket/affiliatePlayedTicket.interface';
import { IAffiliateTicketDA } from '../../models/ticket/affiliateTicketDA.interface';
import { IShopReportDA } from '../../models/shopReport/DAObjects/shopReportDa.interface';
import { IPaidReportsByAffiliateDA } from '../../models/paidReport/DAObjects/paidReportsByAffiliateDA.interface';


@Injectable({
  providedIn: 'root'
})
export class TicketManagerTicketProviderService extends BaseService {

  constructor(
    private http: HttpClient,
    protected loaderService: LoaderService,
    protected userActivityService: UserActivityService,
    protected notificationService: NotificationService,
    protected storage: StorageService
  ) {
    super(loaderService, userActivityService, notificationService, storage);
  }


  getTicketsAFF(loggedAffiliateId: number, shopIds: number[], affiliateIds: number[], dateFrom: Date, dateTo: Date): Observable<IAffiliateTicketDA[]> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getTicketsByFilter`, {
      shopIds,
      affiliateIds,
      affiliateId: loggedAffiliateId,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          return of(null);
        }
      }),
      catchError(error => of(null)));
  }

  // getBetTicketsForAffiliate(affiliateId: number, limit: number, offset: number, dateFrom: Date, dateTo: Date, filter: string): Observable<IPlayedTicketTO[]> {
  //   const params = new HttpParams()
  //     .set('affiliateId', affiliateId.toString())
  //     .set('limit', limit.toString())
  //     .set('offset', offset.toString())
  //     .set('startDate', dateFrom.toISOString().replace('Z', ''))
  //     .set('endDate', dateTo.toISOString().replace('Z', ''))
  //     .set('filter', filter ? filter.toString() : filter);

  //   // if (environment.localDeploy) {
  //   //   return of(ticketsMobile);
  //   // }
  //   const url: string = this.baseUrlAndroid + 'tickets';
  //   return this.http.get<IPlayedTicketTO[]>(url, { headers: this.httpHeaders, params }).pipe(
  //     map(response => response as IPlayedTicketTO[]),
  //     catchError(err => {
  //       throwError(this.handleError(err));
  //       return [];
  //     })
  //   );
  // }

  getTicketsByFilterAndPaging(
    affiliateId: number,
    limit: number,
    offset: number,
    dateFrom: Date,
    dateTo: Date,
    filter: string[]): Observable<{ totalCount: number; tickets: IAffiliateTicketDA[] }> {
    const params = new HttpParams();
    const body = JSON.stringify({
      affiliateId,
      limit,
      offset,
      startDate: dateFrom.toISOString().replace('Z', ''),
      endDate: dateTo.toISOString().replace('Z', ''),
      filter
    });
    const url = `${this.tcketManagerBaseUrl}Ticket/tickets`;
    return this.http.post<IBaseResponse>(url, body, { headers: this.httpHeaders, params }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data as IAffiliateTicketDA[]);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(null);
        }
      }),
      catchError(error => this.handleError(error, true, true)));
  }

  getCommissionReport(affiliateId: number, dateFrom: Date, dateTo: Date): Observable<{ [index: number]: ICommissionReportTO }> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getCommissionReport`, {
      affiliateId,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {

          return of(null);
        }
      }),
      catchError(error => of(null)));
  }

  getCommissionReportEstimate(affiliateId: number, dateFrom: Date, dateTo: Date): Observable<{ [index: number]: ICommissionEstimateReportTO }> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getCommissionReportEstimate`, {
      affiliateId,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          return of(null);
        }
      }),
      catchError(error => of(null)));
  }

  getExternalTicketByTicketNumber(ticketNumber: string): Observable<IPlayedTicketWithGroupsTO> {
    const url: string = `${this.tcketManagerBaseUrl}Ticket/getTicketExternalWOCode/` + ticketNumber;
    return this.http.get<IBaseResponse>(url).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data as IPlayedTicketWithGroupsTO);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(null);
        }
      }),
      catchError(error => this.handleError(error, true, true)));
  }


  stornoTicket(ticketNumber: string, pinCode: string): Observable<boolean> {
    const params = new HttpParams();
    const body = JSON.stringify({
      ticketNumber,
      pinCode
    });
    const url = `${this.tcketManagerBaseUrl}Ticket/stornoTicket`;
    return this.http.post<IBaseResponse>(url, body, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
        if (response?.isSuccess) {
          return of(true);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(false);
        }
      }),
        catchError(error => this.handleError(error, true, true)));
  }

  payoutTicket(ticketNumber: string, pinCode: string): Observable<boolean> {
    const params = new HttpParams();
    const body = JSON.stringify({
      externalTicketId: ticketNumber,
      pinCode
    });
    const url = `${this.tcketManagerBaseUrl}Ticket/payoutTicket`;
    return this.http.post<IBaseResponse>(url, body, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
        if (response?.isSuccess) {
          return of(true);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(false);
        }
      }),
        catchError(error => this.handleError(error, true, true)));
  }

  getExternalAndPlatformTicket(ticketNumber: string, pinCode: string): Observable<{platformTicket: IAffiliateTicketDA; bettingTicket: IPlayedTicketWithGroupsTO}> {
    const params = new HttpParams();
    const body = JSON.stringify({
      ticketNumber,
      pinCode
    });
    const url = `${this.tcketManagerBaseUrl}Ticket/getTicketFull`;
    return this.http.post<IBaseResponse>(url, body, { headers: this.httpHeaders, params })
      .pipe(
        switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          this.openErrorNotification(response.failureReason);
          return of(null);
        }
      }),
        catchError(error => this.handleError(error, true, true)));
  }

  getSalesReport(affiliateIds: number[], dateFrom: Date, dateTo: Date): Observable<ISalesReportsByAffiliateDA[]> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getSalesReport`, {
      affiliateIds,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          return of(null);
        }
      }),
      catchError(error => {
        this.handleError(error, true, true);
        return of(null);
      }));
  }

  getPaidReport(affiliateIds: number[], dateFrom: Date, dateTo: Date): Observable<IPaidReportsByAffiliateDA[]> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getPaidReport`, {
      affiliateIds,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          return of(null);
        }
      }),
      catchError(error => {
        this.handleError(error, true, true);
        return of(null);
      }));
  }

  getShopReport(affiliateId: number, dateFrom: Date, dateTo: Date): Observable<IShopReportDA> {
    const params = new HttpParams();
    return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}Ticket/getCashierShopReport`, {
      affiliateId,
      fromDate: dateFrom.toISOString().replace('Z', ''),
      toDate: dateTo.toISOString().replace('Z', '')
    }).pipe(
      switchMap((response) => {
        if (response?.isSuccess) {
          return of(response.data);
        } else {
          return of(null);
        }
      }),
      catchError(error => {
        this.handleError(error, true, true);
        return of(null);
      }));
  }
}
